import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { usePaymentMethod, usePaymentMethodMutations } from "@/hooks";
import { cycleNoun, formatMoney } from "@/lib/money";
import { ApiError } from "@/services";
import type { AutoChargeAvailability, OrgPaymentMethod } from "@/types/api";
import { CheckCircle2, ExternalLink, Info } from "lucide-react";
import { useState } from "react";

/**
 * How this org pays: the invoice link, or an authorized mandate we pull from.
 *
 * The card's job is to make the trade honest. Authorizing auto-pay is granting
 * standing permission to take money, so the cap, the account and the way out
 * are all stated on the same screen as the switch that turns it on.
 */
export function PaymentMethodCard() {
	const { data, isLoading } = usePaymentMethod();
	const { register, revoke, setMode } = usePaymentMethodMutations();
	const [error, setError] = useState<string | null>(null);
	const [confirmRevoke, setConfirmRevoke] = useState<OrgPaymentMethod | null>(
		null,
	);

	if (isLoading) return <Skeleton className="h-40 w-full" />;
	if (!data) return null;

	const {
		availability,
		payment_method: pm,
		collection_mode: mode,
		billing_phone_on_file: hasPhone,
	} = data;
	const auto = mode === "auto_charge";

	async function run(fn: () => Promise<unknown>) {
		setError(null);
		try {
			await fn();
		} catch (e) {
			setError(
				e instanceof ApiError ? e.message : "Something went wrong. Try again.",
			);
		}
	}

	return (
		<>
			<Card>
				<CardContent className="flex flex-col gap-4 p-5">
					<div className="flex items-start justify-between gap-4">
						<div>
							<h2 className="text-body font-semibold text-heading">
								How you pay
							</h2>
							<p className="mt-1 text-caption text-muted-foreground">
								{auto
									? "Invoices are charged automatically to your authorized account."
									: "Invoices are sent to your billing contact and paid from the link."}
							</p>
						</div>
						{auto ? <Badge variant="active">Automatic</Badge> : null}
					</div>

					{pm && pm.status === "active" ? (
						<ActiveMandate
							pm={pm}
							availability={availability}
							onRevoke={() => setConfirmRevoke(pm)}
							revoking={revoke.isPending}
						/>
					) : pm && pm.status === "pending" ? (
						<PendingMandate pm={pm} />
					) : (
						<Unauthorized
							availability={availability}
							pm={pm}
							hasPhone={hasPhone}
							onRegister={(contact) =>
								run(() => register.mutateAsync(contact ? { contact } : {}))
							}
							registering={register.isPending}
						/>
					)}

					{/* Only offered once a mandate exists — turning this on without
					    one would make every debit fall back on the first sweep. */}
					{pm?.status === "active" ? (
						<div className="flex items-center justify-between gap-4 border-t border-divider pt-4">
							<div>
								<div className="text-body text-heading">
									{auto ? "Automatic payment is on" : "Pay invoices manually"}
								</div>
								<p className="mt-0.5 text-caption text-muted-foreground">
									{auto
										? "You can switch back to paying invoices yourself at any time."
										: "Switch on to have each invoice charged when it is issued."}
								</p>
							</div>
							<Button
								variant={auto ? "outline" : "default"}
								size="sm"
								disabled={setMode.isPending}
								onClick={() =>
									run(() =>
										setMode.mutateAsync(auto ? "invoice" : "auto_charge"),
									)
								}
							>
								{setMode.isPending ? "Saving…" : auto ? "Turn off" : "Turn on"}
							</Button>
						</div>
					) : null}

					{error ? <p className="text-caption text-error">{error}</p> : null}
				</CardContent>
			</Card>

			{/*
			 * Revoking is destructive in the sense that matters here: the org
			 * silently returns to paying by hand, and an admin who does not
			 * realise that is an org that drifts into dunning.
			 */}
			<AlertDialog
				open={confirmRevoke !== null}
				onOpenChange={(o) => !o && setConfirmRevoke(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove this payment method?</AlertDialogTitle>
						<AlertDialogDescription>
							Automatic payment will stop and your invoices will go back to
							being paid from the link we email you.{" "}
							<span className="text-heading">
								Nothing already paid is affected
							</span>
							, and you can authorize a new method whenever you like.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Keep it</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={() => {
								if (confirmRevoke)
									run(() => revoke.mutateAsync(confirmRevoke.id));
								setConfirmRevoke(null);
							}}
						>
							Remove
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

function ActiveMandate({
	pm,
	availability,
	onRevoke,
	revoking,
}: {
	pm: OrgPaymentMethod;
	availability: AutoChargeAvailability;
	onRevoke: () => void;
	revoking: boolean;
}) {
	// A mandate's cap CANNOT be raised — a bigger charge needs a new
	// authorization. So an org whose next charge has grown past the cap must be
	// told now, while they can still act, not when the debit falls back.
	const cap = pm.max_amount_cents;
	const next = availability.expected_debit_cents;
	const outgrown = cap != null && next > cap;

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-center gap-2.5 rounded-md border border-divider bg-surface2 px-3 py-2.5">
				<CheckCircle2 className="size-4 shrink-0 text-success" />
				<div className="min-w-0 flex-1">
					<div className="text-body text-heading">{describeAccount(pm)}</div>
					<div className="text-caption text-muted-foreground">
						Authorized up to {cap == null ? "—" : formatMoney(cap, pm.currency)}{" "}
						per charge
					</div>
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={onRevoke}
					disabled={revoking}
				>
					Remove
				</Button>
			</div>

			{outgrown ? (
				<div className="flex items-start gap-2 rounded-md border border-orange-stroke bg-orange-surface px-3 py-2.5">
					<Info className="mt-0.5 size-4 shrink-0 text-orange-text" />
					<p className="text-caption text-orange-text">
						Your next charge is{" "}
						<strong className="tabular">
							{formatMoney(next, availability.currency)}
						</strong>
						, above the {formatMoney(cap, pm.currency)} you authorized. It will
						be invoiced instead of charged automatically. An authorized limit
						can't be raised — remove this method and authorize a new one to
						auto-pay it.
					</p>
				</div>
			) : null}
		</div>
	);
}

/**
 * Authorization finishes at the bank, in another tab. The link has to stay
 * reachable or an admin who closed that tab has no way back to it.
 */
function PendingMandate({ pm }: { pm: OrgPaymentMethod }) {
	return (
		<div className="flex items-center gap-3 rounded-md border border-orange-stroke bg-orange-surface px-3 py-2.5">
			<div className="min-w-0 flex-1">
				<div className="text-body text-orange-text">
					Waiting for you to authorize
				</div>
				<p className="text-caption text-orange-text">
					Finish approving with your bank. This page updates once it is done.
				</p>
			</div>
			{pm.registration_url ? (
				<Button asChild size="sm" variant="secondary">
					<a href={pm.registration_url} target="_blank" rel="noreferrer">
						Continue <ExternalLink className="ml-1.5 size-3.5" />
					</a>
				</Button>
			) : null}
		</div>
	);
}

function Unauthorized({
	availability,
	pm,
	hasPhone,
	onRegister,
	registering,
}: {
	availability: AutoChargeAvailability;
	pm: OrgPaymentMethod | null;
	hasPhone: boolean;
	onRegister: (contact?: string) => void;
	registering: boolean;
}) {
	const per = cycleNoun(availability.cycle);
	const [phone, setPhone] = useState("");

	// Not available is never a dead end here: the server returns what WOULD fix
	// it — usually "this contract billed monthly would fit under the cap" — and
	// that is a decision the partner can act on with their account manager.
	if (!availability.available) {
		return (
			<div className="flex items-start gap-2 rounded-md border border-divider bg-surface2 px-3 py-2.5">
				<Info className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
				<p className="text-caption text-muted-foreground">
					{availability.suggestion ??
						"Automatic payment isn't available on this contract."}
				</p>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3">
			{/* A previous attempt that failed should say so — otherwise the button
			    just seems not to work. */}
			{pm?.failure_reason ? (
				<p className="text-caption text-error">
					Last attempt failed: {pm.failure_reason}
				</p>
			) : null}
			<p className="text-caption text-muted-foreground">
				Authorize your bank once and each invoice is paid automatically when it
				is issued. Your next {per} is{" "}
				<strong className="tabular text-heading">
					{formatMoney(
						availability.expected_debit_cents,
						availability.currency,
					)}
				</strong>
				. You approve a limit at your bank and can cancel any time.
			</p>
			{/*
			 * The bank needs a number to reach the account holder on, and most
			 * orgs have none on file. Asked for HERE rather than sending the
			 * admin to Settings and back — and kept, so it is typed once and
			 * then used for invoices too.
			 */}
			{hasPhone ? null : (
				<div className="flex flex-col gap-1.5">
					<label htmlFor="billing-phone" className="text-caption text-heading">
						Billing phone
					</label>
					<Input
						id="billing-phone"
						type="tel"
						autoComplete="tel"
						value={phone}
						onChange={(e) => setPhone(e.target.value)}
						placeholder="+91 98765 43210"
						className="max-w-xs"
					/>
					<p className="text-caption text-muted-foreground">
						Your bank needs a number for the account holder. We'll use it on
						your invoices too.
					</p>
				</div>
			)}

			<div>
				<Button
					onClick={() => onRegister(hasPhone ? undefined : phone.trim())}
					disabled={registering || (!hasPhone && phone.trim().length < 6)}
				>
					{registering ? "Setting up…" : "Set up automatic payment"}
				</Button>
			</div>
		</div>
	);
}

/** Name the account so an admin can recognise what they authorized. */
function describeAccount(pm: OrgPaymentMethod): string {
	const label =
		pm.method === "emandate" || pm.method === "nach"
			? "Bank account"
			: pm.method === "upi"
				? "UPI"
				: "Card";
	const parts = [pm.bank_name, pm.last4 ? `•••• ${pm.last4}` : null].filter(
		Boolean,
	);
	return parts.length ? `${label} — ${parts.join(" ")}` : label;
}
